import React, { useState } from 'react';
import { Card, Collapse, Row, Col, Tag, Alert, Divider, Typography, Button } from 'antd';
import { motion } from 'framer-motion';
import { 
  QuestionCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  GlobalOutlined
} from '@ant-design/icons';

const { Panel } = Collapse;
const { Text } = Typography;

interface FAQCategory {
  key: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  questions: {
    question: string;
    answer: string | React.ReactNode;
    important?: boolean;
    tags?: string[];
  }[];
}

const ProfessionalFAQ: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('pricing');

  const faqCategories: FAQCategory[] = [
    {
      key: 'pricing',
      title: '价格时效',
      icon: <SafetyCertificateOutlined />,
      color: '#52c41a',
      questions: [
        {
          question: '美国空运专线多少钱一公斤？',
          answer: (
            <div>
              <p><strong>318空派专线价格体系：</strong></p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li><strong>重量分界：</strong>0.5kg起收，按实重或体积重较大值计费</li>
                <li><strong>时效等级：</strong>5-8天稳定时效，超时赔付保障</li>
                <li><strong>包含服务：</strong>国内取件、报关出口、航空运输、美国清关、末端派送</li>
                <li><strong>增值服务：</strong>代付关税、包装加固、保险服务</li>
              </ul>
              <Alert 
                message="获取精准报价" 
                description="价格因货物类型、目的地、时效要求而异。点击&quot;智能计算运费&quot;获取实时报价，或联系客服获得专属优惠价格。"
                type="success"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          important: true,
          tags: ['价格', '计费', '成本']
        },
        {
          question: '空运到美国需要多长时间？',
          answer: (
            <div>
              <p><strong>318空派时效保障：</strong></p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li><strong>标准时效：</strong>5-8个工作日门到门</li>
                <li><strong>节假日影响：</strong>中美节假日可能延长1-2天</li>
                <li><strong>清关时效：</strong>正常货物24-48小时清关</li>
                <li><strong>特殊情况：</strong>查验货物需要额外2-3天</li>
              </ul>
              <div style={{ background: '#f6ffed', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                <strong>时效承诺：</strong>我们承诺97.2%的货物在8个工作日内送达，超时提供赔付服务。
              </div>
            </div>
          ),
          important: true,
          tags: ['时效', '派送', '保障']
        },
        {
          question: '美国FBA头程空运怎么收费？',
          answer: (
            <div>
              <p><strong>FBA专线服务特色：</strong></p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li><strong>直达服务：</strong>直送亚马逊仓库，无需转运</li>
                <li><strong>标签处理：</strong>免费提供FBA标签贴附服务</li>
                <li><strong>时效优势：</strong>比海运快15-20天，比快递便宜30-40%</li>
                <li><strong>专业包装：</strong>符合亚马逊入仓标准的包装服务</li>
              </ul>
              <Alert 
                message="FBA专车服务" 
                description="针对FBA卖家，我们提供专门的亚马逊仓库配送服务，包括预约入仓、标签处理等增值服务。"
                type="info"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          important: true,
          tags: ['FBA', '亚马逊', '头程']
        }
      ]
    },
    {
      key: 'clearance',
      title: '清关问题',
      icon: <SafetyCertificateOutlined />,
      color: '#1890ff',
      questions: [
        {
          question: '美国FDA认证产品需要提供哪些资料？',
          answer: (
            <div>
              <p>FDA认证产品需要提供以下完整资料：</p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li><strong>FDA厂家备案号码</strong>（FDA factory registration#）</li>
                <li><strong>产品注册号码</strong>（device listing#）</li>
                <li><strong>初始进口商注册号码</strong>（initial US based importer registration#）</li>
              </ul>
              <Alert 
                message="注意" 
                description="所有FDA相关产品必须提供完整的三个注册号码，缺一不可。建议在发货前提前准备好相关文件。"
                type="warning"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          important: true,
          tags: ['FDA', '医疗器械', '食品']
        },
        {
          question: '儿童产品和玩具的清关要求是什么？',
          answer: (
            <div>
              <p>儿童产品和玩具需要满足以下要求：</p>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li><strong>CPSC检测报告</strong> - 必须提供符合美国标准的检测报告</li>
                <li><strong>产品标签要求：</strong>
                  <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                    <li>适用年龄标识</li>
                    <li>厂家及进口商信息</li>
                    <li>中国制造标签</li>
                    <li>溯源标签（tracking label）</li>
                  </ul>
                </li>
              </ul>
              <Alert 
                message="温馨提示" 
                description="所有标签必须贴在产品及其包装上，缺少任何一个标签都可能导致清关延误。"
                type="info"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          important: true,
          tags: ['CPSC', '儿童玩具', '安全标准']
        },
        {
          question: '清关成功率和时效如何保障？',
          answer: (
            <div>
              <p><strong>318空派专线清关保障：</strong></p>
              <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                <Col span={12}>
                  <div style={{ textAlign: 'center', padding: '12px', background: '#f0f9ff', borderRadius: '8px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>97.2%</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>清关成功率</div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ textAlign: 'center', padding: '12px', background: '#f6ffed', borderRadius: '8px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>12-48h</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>快速放行</div>
                  </div>
                </Col>
              </Row>
              <p><strong>四大机场清关优势：</strong></p>
              <div style={{ background: '#fafafa', padding: '12px', borderRadius: '6px', fontSize: '13px' }}>
                • <strong>LAX洛杉矶：</strong>西海岸主要枢纽，清关效率最高<br/>
                • <strong>SFO旧金山：</strong>北加州覆盖，科技产品清关优势<br/>
                • <strong>JFK纽约：</strong>东海岸门户，传统贸易产品专长<br/>
                • <strong>ORD芝加哥：</strong>中部枢纽，全美辐射便利
              </div>
            </div>
          ),
          tags: ['清关成功率', '时效保障', '四大机场']
        }
      ]
    },
    {
      key: 'shipping',
      title: '运输问题',
      icon: <TruckOutlined />,
      color: '#52c41a',
      questions: [
        {
          question: '318空派专线的时效标准是多少？',
          answer: (
            <div>
              <Alert 
                message="时效保障" 
                description="5-8个工作日稳定时效，对比同行7-10天更快"
                type="success"
                showIcon
                style={{ marginBottom: '16px', fontSize: '13px' }}
              />
              <p><strong>详细时效分解：</strong></p>
              <div style={{ background: '#f6ffed', padding: '16px', borderRadius: '8px', fontSize: '13px' }}>
                • <strong>D+1：</strong>国内收货打包<br/>
                • <strong>D+2：</strong>航班起飞<br/>
                • <strong>D+3：</strong>到达美国清关<br/>
                • <strong>D+4-6：</strong>清关放行派送<br/>
                • <strong>D+5-8：</strong>末端派送签收
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                *时效可能受天气、海关查验、节假日等不可抗力因素影响
              </Text>
            </div>
          ),
          important: true,
          tags: ['5-8天', '稳定时效', '超时赔付']
        },
        {
          question: '末端派送方式有哪些选择？',
          answer: (
            <div>
              <p><strong>多样化派送渠道，满足不同需求：</strong></p>
              <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                <Col xs={24} sm={12}>
                  <Card size="small" title="快递派送" style={{ height: '100%' }}>
                    <p style={{ fontSize: '13px', margin: 0 }}>
                      <strong>UPS/FedEx</strong><br/>
                      • 住宅、商业地址均可<br/>
                      • 跟踪信息详细<br/>
                      • 签收便利快捷
                    </p>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card size="small" title="卡车派送" style={{ height: '100%' }}>
                    <p style={{ fontSize: '13px', margin: 0 }}>
                      <strong>专车服务</strong><br/>
                      • 大件货物友好<br/>
                      • 成本相对较低<br/>
                      • 托盘货物专用
                    </p>
                  </Card>
                </Col>
              </Row>
              <Alert 
                message="FBA仓库专车" 
                description="ONT8、FTW1、SMF3等主要FBA仓库提供固定专车派送服务"
                type="info"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          tags: ['UPS', 'FedEx', 'FBA专车', '卡车派送']
        },
        {
          question: '超时赔付政策是怎样的？',
          answer: (
            <div>
              <Alert 
                message="独家超时赔付保障" 
                description="同行都没有的赔付服务，让您的货物运输更有保障"
                type="success"
                showIcon
                style={{ marginBottom: '16px', fontSize: '13px' }}
              />
              <p><strong>赔付标准：</strong></p>
              <ul style={{ paddingLeft: '20px', fontSize: '14px', marginBottom: '16px' }}>
                <li><strong>延误标准：</strong>超过承诺时效2个工作日开始计算</li>
                <li><strong>赔付金额：</strong>按运费的10-30%进行赔付</li>
                <li><strong>免责条款：</strong>海关查验、天气、航班取消等不可抗力除外</li>
              </ul>
              <div style={{ background: '#fff2e8', padding: '12px', borderRadius: '6px', fontSize: '13px' }}>
                <strong>申请流程：</strong>货物超时→联系客服→提供单号→审核后赔付
              </div>
            </div>
          ),
          important: true,
          tags: ['超时赔付', '行业首创', '服务保障']
        }
      ]
    },
    {
      key: 'pricing',
      title: '价格问题',
      icon: <DollarOutlined />,
      color: '#faad14',
      questions: [
        {
          question: '0.5kg起收相比同行有什么优势？',
          answer: (
            <div>
              <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                <Col xs={24} sm={12}>
                  <Card size="small" style={{ background: '#f0f9ff' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                        318空派专线
                      </div>
                      <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a' }}>0.5kg</div>
                      <div style={{ fontSize: '13px', color: '#666' }}>最低起收重量</div>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card size="small" style={{ background: '#fff2e8' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fa8c16', marginBottom: '8px' }}>
                        同行标准
                      </div>
                      <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f5222d' }}>21kg</div>
                      <div style={{ fontSize: '13px', color: '#666' }}>普遍起收重量</div>
                    </div>
                  </Card>
                </Col>
              </Row>
              <Alert 
                message="优势分析" 
                description="小货发货更灵活，降低客户发货门槛，特别适合跨境电商测款和小批量补货"
                type="success"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          important: true,
          tags: ['0.5kg起收', '小货优势', '跨境电商']
        },
        {
          question: '商业地址为什么免附加费？',
          answer: (
            <div>
              <p><strong>行业对比：</strong></p>
              <div style={{ background: '#f6ffed', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                <strong>318空派：</strong>商业地址完全免费，无任何附加费用
              </div>
              <div style={{ background: '#fff1f0', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
                <ExclamationCircleOutlined style={{ color: '#f5222d', marginRight: '8px' }} />
                <strong>同行标准：</strong>非FBA地址统一加收+1元/kg附加费
              </div>
              <Alert 
                message="节省成本" 
                description="对于大量商业地址收货的客户，每年可节省数万元物流成本"
                type="info"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          tags: ['商业地址', '免费政策', '成本优势']
        },
        {
          question: '重货和泡货有什么优惠政策？',
          answer: (
            <div>
              <p><strong>重货优惠政策：</strong></p>
              <div style={{ background: '#f0f9ff', padding: '12px', borderRadius: '6px', marginBottom: '12px', fontSize: '14px' }}>
                • <strong>单票≥50kg：</strong>体积比1:250时，减免1元/kg<br/>
                • <strong>单票≥50kg：</strong>体积比1:300时，减免1.5元/kg
              </div>
              
              <p><strong>泡货优惠政策：</strong></p>
              <div style={{ background: '#fff2e8', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' }}>
                • <strong>单票≥50kg：</strong>分泡比例按50%计算<br/>
                • <strong>大泡货物：</strong>单独商议，提供定制方案
              </div>
              
              <Alert 
                message="专业建议" 
                description="重货和泡货建议在发货前联系客服，我们会根据具体情况提供最优惠的报价方案"
                type="info"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          tags: ['重货优惠', '泡货优惠', '体积比优化']
        }
      ]
    },
    {
      key: 'service',
      title: '服务问题',
      icon: <GlobalOutlined />,
      color: '#722ed1',
      questions: [
        {
          question: '相比国际快递有什么价格优势？',
          answer: (
            <div>
              <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                <Col span={12}>
                  <div style={{ textAlign: 'center', padding: '16px', background: '#f6ffed', borderRadius: '8px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a', marginBottom: '8px' }}>
                      318空派专线
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      价格合理<br/>
                      时效稳定<br/>
                      专业清关
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ textAlign: 'center', padding: '16px', background: '#fff1f0', borderRadius: '8px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#f5222d', marginBottom: '8px' }}>
                      国际快递
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      价格昂贵<br/>
                      波动较大<br/>
                      标准服务
                    </div>
                  </div>
                </Col>
              </Row>
              <Alert 
                message="成本对比" 
                description="相比DHL、FedEx等国际快递，318空派专线平均节省30-50%物流成本，同时提供更稳定的时效"
                type="success"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          tags: ['成本优势', '稳定性', '专业服务']
        },
        {
          question: '是否支持海外仓派送？',
          answer: (
            <div>
              <Alert 
                message="全面支持海外仓派送" 
                description="318空派专线完全支持海外仓派送服务，满足不同客户需求"
                type="success"
                showIcon
                style={{ marginBottom: '16px', fontSize: '13px' }}
              />
              <p><strong>支持的海外仓类型：</strong></p>
              <ul style={{ paddingLeft: '20px', fontSize: '14px', marginBottom: '16px' }}>
                <li><strong>FBA仓库：</strong>亚马逊所有FBA仓库，提供专车派送</li>
                <li><strong>第三方海外仓：</strong>主流海外仓服务商均支持</li>
                <li><strong>自建海外仓：</strong>客户自有仓库地址派送</li>
              </ul>
              <div style={{ background: '#f0f9ff', padding: '12px', borderRadius: '6px', fontSize: '13px' }}>
                <strong>温馨提示：</strong>海外仓派送建议提前确认收货时间和要求，确保仓库能够正常接收货物
              </div>
            </div>
          ),
          tags: ['海外仓', 'FBA', '第三方仓库']
        },
        {
          question: '全程跟踪服务如何实现？',
          answer: (
            <div>
              <p><strong>全程可视化追踪：</strong></p>
              <div style={{ background: '#fafafa', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                <Row gutter={[16, 8]}>
                  <Col span={24}><Text style={{ fontSize: '13px' }}><ClockCircleOutlined style={{ color: '#1890ff' }} /> <strong>收货入库：</strong>实时更新入库状态</Text></Col>
                  <Col span={24}><Text style={{ fontSize: '13px' }}><ClockCircleOutlined style={{ color: '#52c41a' }} /> <strong>出库装机：</strong>航班信息同步推送</Text></Col>
                  <Col span={24}><Text style={{ fontSize: '13px' }}><ClockCircleOutlined style={{ color: '#faad14' }} /> <strong>到港清关：</strong>清关进度实时通报</Text></Col>
                  <Col span={24}><Text style={{ fontSize: '13px' }}><ClockCircleOutlined style={{ color: '#722ed1' }} /> <strong>派送签收：</strong>最终签收状态确认</Text></Col>
                </Row>
              </div>
              <Alert 
                message="多渠道通知" 
                description="支持微信、短信、邮件等多种方式接收货物状态更新通知"
                type="info"
                showIcon
                style={{ fontSize: '13px' }}
              />
            </div>
          ),
          tags: ['全程跟踪', '状态推送', '可视化']
        }
      ]
    }
  ];

  const currentCategory = faqCategories.find(cat => cat.key === activeCategory);

  return (
    <section id="faq" style={{ 
      padding: '80px 0', 
      background: '#f5f5f7',
    }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <h2 className="display-medium" style={{ 
            marginBottom: '24px',
            color: '#1d1d1f'
          }}>
            专业问答中心
          </h2>
          <p className="body-large" style={{ 
            color: '#6e6e73', 
            maxWidth: '800px', 
            margin: '0 auto',
            fontSize: '1.125rem'
          }}>
            基于行业最佳实践，为您解答美国空派专线服务的各类专业问题
          </p>
        </motion.div>

        {/* 分类导航 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: '40px' }}
        >
          <Row gutter={[16, 16]}>
            {faqCategories.map((category) => (
              <Col xs={12} sm={6} key={category.key}>
                <Button
                  type={activeCategory === category.key ? 'primary' : 'default'}
                  size="large"
                  icon={category.icon}
                  onClick={() => setActiveCategory(category.key)}
                  style={{
                    width: '100%',
                    height: 'auto',
                    padding: '16px 12px',
                    background: activeCategory === category.key ? '#0071e3' : '#ffffff',
                    borderColor: activeCategory === category.key ? '#0071e3' : '#d2d2d7',
                    color: activeCategory === category.key ? '#ffffff' : '#6e6e73',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '8px'
                  }}
                >
                  <span style={{ fontSize: '14px', fontWeight: '600' }}>
                    {category.title}
                  </span>
                </Button>
              </Col>
            ))}
          </Row>
        </motion.div>

        {/* FAQ内容 */}
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card
            style={{
              background: '#ffffff',
              border: '1px solid #d2d2d7',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
            }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ color: '#0071e3', fontSize: '20px' }}>
                  {currentCategory?.icon}
                </div>
                <span style={{ color: '#1D1D1F', fontSize: '18px', fontWeight: '600' }}>
                  {currentCategory?.title}
                </span>
              </div>
            }
          >
            <Collapse 
              ghost
              expandIconPosition="end"
              style={{ background: 'transparent' }}
            >
              {currentCategory?.questions.map((faq, index) => (
                <Panel
                  key={index}
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <QuestionCircleOutlined style={{ color: '#0071e3' }} />
                      <span style={{ fontWeight: '500', fontSize: '15px' }}>
                        {faq.question}
                      </span>
                      {faq.important && (
                        <Tag color="red" style={{ fontSize: '12px', padding: '2px 6px' }}>重要</Tag>
                      )}
                    </div>
                  }
                  style={{
                    marginBottom: '8px',
                    background: faq.important ? '#fff7e6' : 'transparent',
                    border: faq.important ? '1px solid #ffd591' : 'none',
                    borderRadius: '8px'
                  }}
                >
                  <div style={{ paddingLeft: '28px' }}>
                    {typeof faq.answer === 'string' ? (
                      <p style={{ color: '#666', lineHeight: '1.6', margin: 0 }}>
                        {faq.answer}
                      </p>
                    ) : (
                      faq.answer
                    )}
                    
                    {faq.tags && (
                      <div style={{ marginTop: '12px' }}>
                        {faq.tags.map((tag, tagIndex) => (
                          <Tag key={tagIndex} color="blue" style={{ fontSize: '12px' }}>
                            {tag}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </div>
                </Panel>
              ))}
            </Collapse>
          </Card>
        </motion.div>

        {/* 联系我们提示 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{ marginTop: '40px' }}
        >
          <Alert
            message="还有其他问题？"
            description="如果以上问答无法解决您的疑问，欢迎联系我们的专业客服团队，我们将为您提供一对一的详细解答。"
            type="info"
            showIcon
            icon={<FileTextOutlined />}
            action={
              <Button size="small" type="primary">
                联系客服
              </Button>
            }
            style={{
              background: '#ffffff',
              border: '1px solid #d2d2d7',
            }}
          />
        </motion.div>
      </div>
    </section>
  );
};

export default ProfessionalFAQ;
